import type React from "react";
import { DocumentLayout } from "~/components/Layout";
import "./style.css";

export const TermsEnglish: React.FC = () => (
	<DocumentLayout>
		<main className="document-main-content">
			<h1 className="document-h1 mb-14">Web.Auto Terms and Conditions</h1>
			<div className="col-10 mx-auto">
				<p>
					These Terms and Conditions set forth the terms and conditions for
					providing and using the Service (as defined in Article 2 below). The
					Applicant must read and agree to the entire Terms and Conditions
					before it is registered as the User.
				</p>

				<h2 className="document-h2 mt-12">Chapter 1. Purpose and Definition</h2>
				<article className="mb-8">
					<h3 className="my-4">
						Article 1 <span className="text-decoration-underline">Purpose</span>
					</h3>
					<p>
						The purpose of these Terms and Conditions is to set forth the terms
						and conditions for providing and using the Service, and the rights
						and obligations of the Company and the Users relating to the
						Service. These Terms and Conditions shall apply to all aspects of
						the relationship between the Company and each User in connection
						with the Service.
					</p>
				</article>
				<article className="mb-8">
					<h3 className="my-4">
						Article 2{" "}
						<span className="text-decoration-underline">Definitions</span>
					</h3>
					<p>
						In these Terms and Conditions, the following capitalized terms have
						the following meanings.
					</p>
					<ol className="list-parentheses">
						<li>
							<strong>"Accounts"</strong> means the accounts granted by the
							Company to the Users under the name "TIER IV Accounts" which
							enable the Users to use the Service.
						</li>
						<li>
							<strong>"Antisocial Force"</strong> shall have the meaning set
							forth in Article 3.3(3).
						</li>
						<li>
							<strong>"Applicant"</strong> shall have the meaning set forth in
							Article 3.1.
						</li>
						<li>
							<strong>"Cause of Refusal"</strong> shall mean the matters set
							forth in each item of Article 3.3.
						</li>
						<li>
							<strong>"Company"</strong> means TIER IV, Inc.
						</li>
						<li>
							<strong>"Company Indemnified Parties"</strong> shall have the
							meaning set forth in Article 12.4.
						</li>
						<li>
							<strong>"Content"</strong> shall mean videos, still images, audio,
							text, graphics, logos, button icons, text, and any other data.
						</li>
						<li>
							<strong>"Free Features"</strong> shall have the meaning set forth
							in Article 4(1).
						</li>
						<li>
							<strong>"Integration Tool"</strong> means a tool that becomes
							available on the System by integrating with the Service.
						</li>
						<li>
							<strong>"IP Rights"</strong> means copyrights, patents,
							trademarks, utility rights, design rights and other intellectual
							property rights (including rights to obtain or to apply for
							registration of such rights) in any country.
						</li>
						<li>
							<strong>"Paid Features"</strong> shall have the meaning set forth
							in Article 4(2).
						</li>
						<li>
							<strong>"Paid Feature Licensing Agreement"</strong> shall have the
							meaning set forth in Article 4(2).
						</li>
						<li>
							<strong>"Password"</strong> shall have the meaning set forth in
							Article 5.1.
						</li>
						<li>
							<strong>"Person"</strong> means any person including an
							individual, corporation and any other entity (whether or not
							having independent legal personality).
						</li>
						<li>
							<strong>"Privacy Policy"</strong> shall have the meaning set forth
							in Article 15.1.
						</li>
						<li>
							<strong>"Registration Information"</strong> shall have the meaning
							set forth in Article 3.1.
						</li>
						<li>
							<strong>"Rules"</strong> shall have the meaning set forth in
							Article 7(1).
						</li>
						<li>
							<strong>"Service"</strong> means Web.Auto, a cloud service
							provided by the Company on the System (as defined below) to
							conduct the development and operation management, etc. of
							automated driving systems.
						</li>
						<li>
							<strong>"System"</strong> means the system operated and managed by
							the Company.
						</li>
						<li>
							<strong>"User"</strong> means any person registered as a user of
							the Service pursuant to Article 3.
						</li>
						<li>
							<strong>"User Data"</strong> means the content and other data
							uploaded to the cloud by the Users using the Service.
						</li>
						<li>
							<strong>"User ID"</strong> shall have the meaning set forth in
							Article 5.1.
						</li>
						<li>
							<strong>"Website"</strong> means the website of the Company,
							related to the Service.
						</li>
					</ol>
				</article>

				<h2 className="document-h2 mt-12">Chapter 2. User</h2>
				<article className="mb-8">
					<h3 className="my-4">
						Article 3{" "}
						<span className="text-decoration-underline">
							Account Registration
						</span>
					</h3>
					<ol className="list-decimal pl-6">
						<li>
							Any Person wishing to use the Service shall apply for the
							registration of an Account to use the Service and provide certain
							information in the form specified by the Company (the{" "}
							<b>"Registration Information"</b>). Any Person filing such an
							application (the <b>"Applicant"</b>) shall be deemed to have
							agreed to comply with these Terms and Conditions.
						</li>
						<li>
							The registration of the Applicant's Account shall be completed at
							the time when the Applicant has completed all the registration
							procedures required by the Company, and the Applicant may commence
							the use of the Service as a User pursuant to these Terms and
							Conditions upon the completion of the Account registration. The
							Applicant will be granted only one Account for the access to the
							Service, unless otherwise agreed.
						</li>
						<li>
							The Company may refuse the application or registration of any
							Applicant for any reason without disclosing such reason. In
							particular, the Company may refuse the registration if:
							<ol className="list-sub-parentheses">
								<li>
									any of the Registration Information provided by the Applicant
									to the Company is found to be false, inaccurate, misleading or
									omitted;
								</li>
								<li>
									the Applicant is a minor, adult ward (
									<i>seinen-hi-koukennin</i>) or a person under curatorship (
									<i>hi-hosanin</i>) or assistance (<i>hi-hojonin</i>), or any
									other individual whose legal capacity is limited under any
									law, unless any necessary consent of a statutory agent of such
									Applicant is duly obtained;
								</li>
								<li>
									the Applicant (i) is an organized crime group (
									<i>bouryokudan</i>) or a member thereof (<i>bouryokudanin</i>
									), extreme rightist organization, or any other anti-social
									force (collectively, the <b>"Antisocial Force"</b>), (ii) has
									any interaction or involvement with the Antisocial Force, or
									(iii) assists or is involved in the maintenance, operation or
									management of the Antisocial Force by way of financing or
									other means; or
								</li>
								<li>
									the application or registration of the Applicant had been
									previously terminated by the Company.
								</li>
							</ol>
						</li>
					</ol>
				</article>

				<article className="mb-8">
					<h3 className="my-4">
						Article 4{" "}
						<span className="text-decoration-underline">Usage Fee</span>
					</h3>
					<ol className="list-decimal pl-6">
						<li>
							The Users may freely use the features separately designated by the
							Company as free features (the <b>"Free Features"</b>) in
							accordance with these Terms and Conditions. There shall be no
							usage fee for the Free Features.
						</li>
						<li>
							If the User separately enters into a licensing agreement (the{" "}
							<b>"Paid Feature Licensing Agreement"</b>) with the Company, the
							User may use the features separately designated by the Company as
							paid features (the <b>"Paid Features"</b>). The usage fee for the
							Paid Features shall be provided in the Paid Feature Licensing
							Agreement.
						</li>
					</ol>
				</article>

				<h2 className="document-h2 mt-12">Chapter 3. Covenants of Users</h2>
				<article className="mb-8">
					<h3 className="my-4">
						Article 5{" "}
						<span className="text-decoration-underline">
							User ID and Password
						</span>
					</h3>
					<ol className="list-decimal pl-6">
						<li>
							The Company shall provide a user ID (the <b>"User ID"</b>) to each
							User upon completion of the Account registration pursuant to
							Article 3. The User shall be responsible for keeping and
							maintaining the User ID, together with a password for using the
							Service specified by the User (the <b>"Password"</b>),
							confidentially and in an appropriate manner.
						</li>
						<li>
							The User may not disclose the User ID or the Password to a third
							party, or allow a third party to use them. When the User ID or the
							Password is used, the Company may deem that the User used it. The
							Company shall in no event be liable for any damage, loss, cost or
							expense to the User arising out of or in connection with
							inappropriate maintenance or usage of the User ID or the Password
							by the User or their misuse by a third party.
						</li>
						<li>
							The User shall not concurrently access the Service from more than
							one computer or other device at the same time using the same
							Account.
						</li>
					</ol>
				</article>

				<article className="mb-8">
					<h3 className="my-4">
						Article 6{" "}
						<span className="text-decoration-underline">
							Change in Registration Information
						</span>
					</h3>
					<p>
						The Users shall promptly notify the Company of any change to the
						Registration Information in the manner specified by the Company.
					</p>
				</article>

				<article className="mb-8">
					<h3 className="my-4">
						Article 7{" "}
						<span className="text-decoration-underline">
							Obligations of the Users
						</span>
					</h3>
					<p>The Users shall comply with the following obligations:</p>
					<ol className="list-parentheses">
						<li>
							comply with all applicable laws and regulations in all relevant
							jurisdictions (including those relevant to personal information),
							these Terms and Conditions and any other rules for the use of the
							Service established by the Companies (the <b>"Rules"</b>);
						</li>
						<li>
							not infringe upon any IP Rights, portrait rights, privacy rights,
							reputation or other rights of the Company, other Users or third
							parties;
						</li>
						<li>
							not commit any crime (including fraud and duress) or act against
							the public order or good moral values;
						</li>
						<li>
							not use the User ID or Password of other Users, or access or
							attempt to access the system or network of the Service improperly;
						</li>
						<li>
							when using the Integration Tool, comply with the terms of use of
							the Integration Tool and other rules concerning the use of the
							Integration Tool;
						</li>
						<li>
							when using the Paid Features, comply with the provisions of the
							Paid Feature Licensing Agreement;
						</li>
						<li>
							not reproduce, adapt or otherwise alter, or publicly transmit the
							Service;
						</li>
						<li>not sell or sublicense the right to use the Service;</li>
						<li>
							not reverse engineer, decompile, or disassemble part or all of the
							Service, or otherwise analyze the Service such as by extracting
							the source code;
						</li>
						<li>
							not post, upload or transmit any of the following:
							<ol className="list-decimal-without-dot">
								<li>computer viruses or other hazardous computer programs;</li>
								<li>
									content which contains the personal information or data of any
									third party;
								</li>
								<li>
									content which may harm the reputation or the credit of the
									Company, other Users or third parties;
								</li>
								<li>
									content which is violent, cruel, sexual, indecent or
									antisocial in its nature or encourages any crime or
									discrimination; or
								</li>
								<li>
									content that constitutes advertisement, solicitation or
									marketing.
								</li>
							</ol>
						</li>
						<li>
							not place an excessive burden on the Service network or system, or
							otherwise interrupt the operation of the Service; and
						</li>
						<li>
							not engage in any other conduct that the Company reasonably deems
							inappropriate.
						</li>
					</ol>
				</article>

				<article className="mb-8">
					<h3 className="my-4">
						Article 8{" "}
						<span className="text-decoration-underline">
							IP Rights and Contents Posted on the Website by the Users
						</span>
					</h3>
					<ol className="list-decimal pl-6">
						<li>
							Any IP Rights related to the Website and the Service are expressly
							reserved by the Company or its licensor. Nothing contained herein
							shall be construed as granting the Users a license to the IP
							Rights owned by the Company or its licensor regarding the Users'
							use of the Service beyond the scope set forth in these Terms and
							Conditions.
						</li>
						<li>
							Notwithstanding the provisions of the preceding paragraph, IP
							Rights in the User Data shall belong to the User who uploaded such
							User Data or a third party who grants the right to such User. The
							Company may store on servers managed by the Company or use the
							User Data to the extent necessary to provide the Service, and the
							Users shall grant the Company a non-exclusive license to the User
							Data to the extent necessary for such storage or use.
						</li>
						<li>
							The Company may analyze and quantify the Users' use of the Service
							(including the User Data) and use such analysis and statistics for
							the operation, improvement, sales and marketing of the Service and
							the development of new services of the Company.
						</li>
					</ol>
				</article>

				<h2 className="document-h2 mt-12">
					Chapter 4. Suspension, Cancellation, Withdrawal and Modification
				</h2>
				<article className="mb-8">
					<h3 className="my-4">
						Article 9{" "}
						<span className="text-decoration-underline">
							Suspension of the Service
						</span>
					</h3>
					<ol className="list-decimal pl-6">
						<li>
							The Company shall be entitled to, without any notice to the Users,
							temporarily suspend or discontinue the Service in whole or in
							part, for a period the Company believes necessary, if:
							<ol className="list-sub-parentheses">
								<li>
									any inspection or maintenance of the computer system utilized
									for the Service is necessary due to urgent or inevitable
									circumstances;
								</li>
								<li>
									computers or communication lines are disrupted due to an
									accident or other unforeseeable event;
								</li>
								<li>
									the Company is unable to provide the Service due to force
									majeure, including but not limited to earthquakes, lightning,
									fire, storm and flood damage, power blackouts and other
									natural disasters; or
								</li>
								<li>
									the Company reasonably determines that suspension or
									discontinuance is required for any other reasons.
								</li>
							</ol>
						</li>
						<li>
							Under no circumstances shall the Company be liable for any damages
							incurred by the Users arising out of any measures taken by the
							Company pursuant to this Article.
						</li>
					</ol>
				</article>

				<article className="mb-8">
					<h3 className="my-4">
						Article 10{" "}
						<span className="text-decoration-underline">
							Registration Cancellation
						</span>
					</h3>
					<ol className="list-decimal pl-6">
						<li>
							The Users may cancel their registration as Users at any time by
							giving notice to the Company or in any other manner specified by
							the Company.
						</li>
						<li>
							The Company may, without notice or demand, temporarily suspend the
							use of the Service by the Users or cancel their registration as
							Users, if:
							<ol className="list-sub-parentheses">
								<li>the Users breach any provision hereof;</li>
								<li>
									the Users fall within any Cause of Refusal at the time of the
									registration of the Users or thereafter;
								</li>
								<li>
									the Users have not used the Service for twelve (12) months or
									more; or
								</li>
								<li>
									the Users do not respond to any reasonable inquiry or other
									request from the Company within thirty (30) days;
								</li>
							</ol>
						</li>
						<li>
							The Users may not view the User Data using the Service after the
							termination of the Users’ registration or suspension of the use of
							the Service by the Users. The Users shall take necessary measures
							such as backing up the User Data at their own responsibility.
						</li>
						<li>
							The Company shall not be liable for any damages incurred by the
							Users arising out of, or in connection with, the termination of
							the Users’ registration or suspension of the use of the Service by
							the Users in any circumstances.
						</li>
					</ol>
				</article>

				<article className="mb-8">
					<h3 className="my-4">
						Article 11{" "}
						<span className="text-decoration-underline">
							Service Modification and Termination
						</span>
					</h3>
					<ol className="list-decimal pl-6">
						<li>
							The Company, in its sole discretion, shall be entitled to modify
							or terminate the Service at any time. The Company shall use its
							reasonable efforts to notify the Users in advance of any intended
							modification or termination of the Service.
						</li>
						<li>
							The Company shall not be liable for any damages incurred by the
							Users arising out of, or in connection with, such modification or
							termination of the Service by the Company.
						</li>
					</ol>
				</article>

				<h2 className="document-h2 mt-12">Chapter 5. Miscellaneous</h2>
				<article className="mb-8">
					<h3 className="my-4">
						Article 12{" "}
						<span className="text-decoration-underline">
							Disclaimer and Waiver of Warranties
						</span>
					</h3>
					<ol className="list-decimal pl-6">
						<li>
							THE COMPANY MAKES NO REPRESENTATION OR WARRANTY OF ANY KIND,
							EXPRESS OR IMPLIED, WITH RESPECT TO THE SERVICE (including but not
							limited to any representation or warranty (i) of fitness or
							suitability for a particular purpose contemplated by the Users,
							(ii) of the expected functions, commercial value, accuracy, or
							usefulness of the Service, (iii) that the use by the Users of the
							Service complies with the laws and regulations applicable to the
							Users or any internal rules established by industrial
							organizations, (iv) that the Service will be free of interruption
							or defects, (v) of the non-infringement of any third party's
							right, (vi) that the Service, its servers and any emails sent by
							the Company contain no viruses or other dangerous computer
							programs and (vii) the accuracy of the content of the Service and
							the Website).
						</li>
						<li>
							Under no circumstances shall the Company be liable for incidental,
							indirect, special or future damages or loss of profits in relation
							to damages incurred by the Users that are attributable to the
							Company for any reasons whatsoever.
						</li>
						<li>
							The Company shall not in any way be liable for transactions,
							communications or disputes between the Users and other Users or a
							third party in connection with the Service or the Website.
						</li>
						<li>
							Users shall be entirely responsible for their use of any content
							of the Service or the Website. If the Users are held responsible
							in a civil lawsuit with third parties such as a claim for damages
							or even in a criminal case, the Company and its subsidiaries and
							other affiliated companies and their directors, officers,
							employees and agents (collectively, the{" "}
							<b>"Company Indemnified Parties"</b>) shall be completely
							discharged from any rights or assertions the Users may have
							against the Company Indemnified Parties, or from any legal action,
							litigation or similar procedures.
						</li>
					</ol>
				</article>

				<article className="mb-8">
					<h3 className="my-4">
						Article 13{" "}
						<span className="text-decoration-underline">Indemnity</span>
					</h3>
					<p>
						The Users shall indemnify and hold the Company Indemnified Parties
						harmless from any of their damages, losses, liabilities, costs or
						expenses (including attorneys' fees), or any claims or demands made
						against the Company Indemnified Parties by any third party, due to
						or arising out of or in connection with breach of any provisions
						(including the representations and warranties) hereof or violation
						of any applicable laws by the Users, or their use of any content of
						the Service or the Website.
					</p>

					<h3 className="my-4">
						Article 14{" "}
						<span className="text-decoration-underline">Confidentiality</span>
					</h3>
					<p>
						Unless the Users obtain the prior written consent of the Company,
						the Users shall keep confidential any and all non-public information
						disclosed by the Company to the Users in connection with the
						Service.
					</p>

					<h3 className="my-4">
						Article 15{" "}
						<span className="text-decoration-underline">User Information</span>
					</h3>
					<p>
						The Company shall keep and use the information of the Users or
						former Users in accordance with the privacy policy separately
						established by the Company (the <b>"Privacy Policy"</b>).
					</p>
				</article>

				<article className="mb-8">
					<h3 className="my-4">
						Article 16{" "}
						<span className="text-decoration-underline">
							Amendments to these Terms and Conditions and Rules
						</span>
					</h3>
					<ol className="list-decimal pl-6">
						<li>
							The Company may amend these Terms and Conditions at any time
							without the prior consent of the Users if:
							<ol className="list-sub-parentheses">
								<li>
									the amendment to these Terms and Conditions is in the general
									interest of the Users; or
								</li>
								<li>
									the amendment to these Terms and Conditions is consistent with
									the purpose of these Terms and Conditions and reasonable in
									light of the circumstances of the amendment, including the
									necessity of the amendment and the reasonableness of the
									contents after the amendment.
								</li>
							</ol>
						</li>
						<li>
							If the Company amends these Terms and Conditions pursuant to the
							provisions of the preceding paragraph, it shall notify the Users
							of the fact that these Terms and Conditions are to be amended, the
							details of the amendment and the effective date thereof on the
							Website or in accordance with Article 17 by the effective date of
							such amendment.
						</li>
						<li>
							Unless otherwise provided by the Rules or other agreement relating
							to the Service between the Company and the Users (if any), if
							there is any conflict between these Terms and Conditions and the
							Rules or such other agreement, these Terms and Conditions shall
							prevail.
						</li>
					</ol>
				</article>

				<article className="mb-8">
					<h3 className="my-4">
						Article 17 <span className="text-decoration-underline">Notice</span>
					</h3>
					<ol className="list-decimal pl-6">
						<li>
							Unless otherwise specified by the Company, all notices and other
							communications from the Users to the Company (including inquiries
							regarding the Service) shall be made by e-mail to the e-mail
							address specified by the Company or in the form provided on the
							Website.
						</li>
						<li>
							Unless otherwise specified in these Terms and Conditions, all
							notices and other communications from the Company to the Users
							shall be made by e-mail. If the Company sends any notice or
							communication by e-mail to the e-mail address included in the
							Registration Information of the Users, the Users shall be deemed
							to have immediately received such notice or communication
							regardless of whether they have actually received it or not.
						</li>
					</ol>
				</article>

				<article className="mb-8">
					<h3 className="my-4">
						Article 18{" "}
						<span className="text-decoration-underline">Assignment</span>
					</h3>
					<ol className="list-decimal pl-6">
						<li>
							The Users shall not assign, transfer, grant security interests in
							or otherwise dispose of their status, rights or obligations under
							these Terms and Conditions without the prior written consent of
							the Company.
						</li>
						<li>
							In cases where the Company assigned the business regarding the
							Service to a third party, the Company may, as part of such
							assignment, assign to such third party its status, rights and
							obligations under these Terms and Conditions, and the Registration
							Information and other information relating to the Users, and the
							Users hereby agrees to such assignment in advance.
						</li>
					</ol>
				</article>

				<article className="mb-8">
					<h3 className="my-4">
						Article 19{" "}
						<span className="text-decoration-underline">Severability</span>
					</h3>
					<p>
						If any provision hereof or part thereof is held to be invalid or
						unenforceable under the laws of Japan or other jurisdiction, the
						other provisions hereof shall remain in full force and effect.
					</p>
				</article>

				<article className="mb-8">
					<h3 className="my-4">
						Article 20{" "}
						<span className="text-decoration-underline">
							Governing Law and Jurisdiction
						</span>
					</h3>
					<ol className="list-decimal pl-6">
						<li>
							These Terms and Conditions shall be governed by the laws of Japan.
						</li>
						<li>
							Any and all disputes arising out of or in connection with these
							Terms and Conditions shall be submitted to the exclusive
							jurisdiction of the Tokyo District Court in the first instance.
						</li>
					</ol>
				</article>

				<article className="mb-8">
					<h3 className="my-4">
						Article 21{" "}
						<span className="text-decoration-underline">Language</span>
					</h3>
					<p>
						English shall be the governing language of these Terms and
						Conditions, and any translation thereof into any other language will
						be prepared for reference purposes only and shall not be binding
						upon any party.
					</p>
				</article>

				<p>Established on February 28 2023</p>
			</div>
		</main>
	</DocumentLayout>
);
